import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  DatePicker,
  Input,
  Label,
  Modal,
  Select,
  Text,
} from '@mercai/clever'

import { generateContainerClasses } from './supply-payment-required-modal.constants'

import { GenerateSupplierOrderButtonProps } from './supply-payment-required-modal.types'

import {
  SupplierPaymentPriorityEnum,
  SupplyPaymentRequiredPayload,
} from '@/types'

import styles from './supply-payment-required-modal.module.css'

export const SupplyPaymentRequiredModal = ({
  isOpen,
  onSupplyPaymentRquired,
  isLoading,
}: GenerateSupplierOrderButtonProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [isOpenModal, setIsOpenModal] = useState(false)

  const [requestLoading, setRequestLoading] = useState(false)

  const [values, setValues] = useState<
    Omit<SupplyPaymentRequiredPayload, 'supplyOrdersReference'>
  >({})

  const onConfirm = async () => {
    setRequestLoading(true)
    const response = await onSupplyPaymentRquired(values)
    if (response) {
      setIsOpenModal(false)
    }
    setRequestLoading(false)
  }

  const onChangeValues = (key: string, value: string | undefined) => {
    setValues((oldValues) => ({
      ...oldValues,
      [key]: value,
    }))
  }

  const onCancel = () => {
    setValues({})
    setIsOpenModal(false)
    setRequestLoading(false)
  }

  return (
    <>
      <div className={generateContainerClasses(isOpen)}>
        <div className={styles.message}>
          <Text>{t(`supplyPaymentRequiredModalMessage`)}</Text>
          <Button
            variant="primary"
            onClick={() => setIsOpenModal(true)}
            disabled={isLoading}
            isActive={!isLoading}
          >
            {t(`supplyPaymentRequiredModalButton`)}
          </Button>
        </div>
      </div>

      <Modal.Root isOpen={isOpenModal} onHandleOpen={onCancel} size="small">
        <Modal.Header
          title={t('supplyPaymentRequiredModalModalTitle')}
          description={t('supplyPaymentRequiredModalModalDescription')}
          className={styles['modal-header']}
        />
        <Modal.Body className={styles['modal-body']}>
          <div className={styles['modal-component']}>
            <div>
              <Label>{t('supplyPaymentRequiredModalModalExpectedPaid')}</Label>
              <DatePicker
                value={values?.expectedPaymentDate}
                onChange={(val) => onChangeValues('expectedPaymentDate', val)}
                placeholder="DD/MM/YYYY"
              />
            </div>

            <div>
              <Label>
                {t('supplyPaymentRequiredModalModalPaymentPriority')}
              </Label>
              <Select
                allowEmpty
                value={values?.paymentPriority}
                options={Object.keys(SupplierPaymentPriorityEnum).map(
                  (key) => ({
                    label: tEnums(`supply_payment_priority_${key}`),
                    value: key,
                  }),
                )}
                onChange={(val) => onChangeValues('paymentPriority', val)}
                placeholder={t('supplyPaymentRequiredModalModalPlaceholder')}
              />
            </div>

            <div>
              <Label>{t('supplyPaymentRequiredModalModalObservations')}</Label>
              <Input
                value={values.observation}
                onChange={(val) => onChangeValues('observation', val)}
              />
            </div>

            <div className={styles['modal-footer']}>
              <Button variant="secondary" onClick={onCancel}>
                {t('supplyPaymentRequiredModalModalCancel')}
              </Button>

              <Button
                variant="primary"
                onClick={onConfirm}
                isLoading={requestLoading}
                isActive={!requestLoading}
              >
                {t('supplyPaymentRequiredModalModalConfirm')}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal.Root>
    </>
  )
}
